import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES } from "../lib/format";
import ListingCard from "../components/ListingCard";

export default function Home() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({});
  const [activeCat, setActiveCat] = useState("all");
  const [query, setQuery] = useState("");
  const [favIds, setFavIds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/listings", { params: { featured: true, limit: 8 } }),
      api.get("/listings/stats"),
    ]).then(([f, s]) => {
      setFeatured(f.data);
      setStats(s.data);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user) api.get("/favorites/ids").then((r) => setFavIds(r.data)).catch(() => {});
  }, [user]);

  const toggleFav = useCallback(async (id) => {
    if (!user) { navigate("/login"); return; }
    const res = await api.post(`/favorites/${id}`);
    setFavIds((prev) => (res.data.favorited ? [...prev, id] : prev.filter((x) => x !== id)));
  }, [user, navigate]);

  const doSearch = () => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (activeCat !== "all") params.set("category", activeCat);
    navigate(`/browse?${params.toString()}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[88vh] min-h-[600px] flex items-end">
        <img
          src="https://images.unsplash.com/photo-1706808849780-7a04fbac83ef"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-5 md:px-10 pb-16">
          <div className="max-w-3xl fade-up">
            <span className="inline-block text-gold text-xs uppercase tracking-[0.3em] mb-5 border border-gold/40 px-4 py-1.5">
              {lang === "en" ? "Land · Property · Mobility" : "Terrain · Immobilier · Mobilité"}
            </span>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl text-white leading-[1.05] tracking-tight">
              {t("hero_title")}
            </h1>
            <p className="text-white/60 text-base md:text-lg mt-6 max-w-xl leading-relaxed">{t("hero_sub")}</p>
          </div>

          {/* Search bar */}
          <div className="mt-10 glass border border-white/15 p-3 md:p-4 max-w-4xl fade-up" style={{ animationDelay: "150ms" }}>
            <div className="flex flex-wrap gap-2 mb-3">
              <CatPill label={t("all")} active={activeCat === "all"} onClick={() => setActiveCat("all")} testid="hero-cat-all" />
              {CATEGORIES.map((c) => (
                <CatPill key={c.key} label={c[lang]} active={activeCat === c.key} onClick={() => setActiveCat(c.key)} testid={`hero-cat-${c.key}`} />
              ))}
            </div>
            <div className="flex gap-2">
              <div className="flex-1 flex items-center gap-2 bg-black/40 border border-white/10 px-4">
                <Search size={18} className="text-white/40" />
                <input
                  data-testid="hero-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && doSearch()}
                  placeholder={t("search_placeholder")}
                  className="flex-1 bg-transparent py-3 text-white placeholder-white/40 outline-none text-sm"
                />
              </div>
              <button data-testid="hero-search-btn" onClick={doSearch} className="bg-gold text-black px-6 font-medium hover:bg-gold-light transition-colors flex items-center gap-2">
                <span className="hidden sm:inline">{t("search_btn")}</span><Search size={18} className="sm:hidden" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Enticing intro band */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pt-16">
        <div className="grid md:grid-cols-3 gap-8">
          {[t("entice1"), t("entice2"), t("entice3")].map((sentence, i) => (
            <div key={i} className="border-l-2 border-gold/50 pl-5 fade-up" style={{ animationDelay: `${i * 100}ms` }} data-testid={`entice-${i}`}>
              <span className="font-display text-gold text-2xl">0{i + 1}</span>
              <p className="text-white/70 leading-relaxed mt-2">{sentence}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16">
        <h2 className="font-display text-2xl md:text-3xl text-white mb-8">{t("explore_cat")}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.key}
                data-testid={`cat-tile-${c.key}`}
                onClick={() => navigate(`/browse?category=${c.key}`)}
                className="group bg-[#121212] border border-white/10 hover:border-gold p-6 flex flex-col items-center gap-3 transition-all hover:-translate-y-1"
              >
                <Icon size={28} strokeWidth={1.4} className="text-gold" />
                <span className="text-white/80 text-sm group-hover:text-white">{c[lang]}</span>
                <span className="text-white/30 text-xs">{stats[c.key] || 0}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-white">{t("featured")}</h2>
            <p className="text-white/40 text-sm mt-1">{t("featured_sub")}</p>
          </div>
          <button onClick={() => navigate("/browse")} data-testid="browse-all-btn" className="text-gold text-sm flex items-center gap-1 hover:gap-2 transition-all">
            {t("browse_all")} <ArrowRight size={16} />
          </button>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => <div key={i} className="aspect-[4/3] shimmer" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((l, i) => (
              <ListingCard key={l.id} listing={l} index={i} favorited={favIds.includes(l.id)} onToggleFav={toggleFav} />
            ))}
          </div>
        )}
      </section>

      {/* Seller CTA */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16">
        <div className="relative overflow-hidden border border-white/10 bg-[#121212]">
          <img src="https://images.pexels.com/photos/8082328/pexels-photo-8082328.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
          <div className="relative z-10 p-10 md:p-16 max-w-xl">
            <h2 className="font-display text-3xl md:text-4xl text-white leading-tight">{t("cta_title")}</h2>
            <p className="text-white/60 mt-4">{t("cta_sub")}</p>
            <button onClick={() => navigate("/post")} data-testid="cta-post-btn" className="mt-8 bg-gold text-black px-7 py-3 font-medium hover:bg-gold-light transition-colors inline-flex items-center gap-2">
              {t("cta_btn")} <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function CatPill({ label, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-xs uppercase tracking-wider px-4 py-2 transition-colors border ${active ? "bg-gold text-black border-gold" : "border-white/15 text-white/70 hover:border-white/40"}`}
    >
      {label}
    </button>
  );
}
